import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar, CheckCircle2, Loader2, Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CalendarConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/calendar/status");
      setStatus(data);
    } catch {
      setStatus({ platform_enabled: false, connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/calendar/status");
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled)
          setStatus({ platform_enabled: false, connected: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Surface callback toasts from the OAuth redirect
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("calendar");
    if (flag === "connected") toast.success("Google Calendar connected");
    if (flag === "error") toast.error("Could not connect Google Calendar");
    if (flag === "not_configured")
      toast.error("Google Calendar is not configured on this server");
    if (flag) {
      url.searchParams.delete("calendar");
      window.history.replaceState({}, "", url.toString());
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/calendar/connect");
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Could not start OAuth");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not start Google OAuth"
      );
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await api.delete("/calendar/disconnect");
      toast.success("Disconnected");
      load();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  if (loading) {
    return (
      <div
        data-testid="settings-calendar-card"
        className="mt-8 flex h-24 items-center justify-center border border-slate-200 bg-white text-slate-400"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div
      data-testid="settings-calendar-card"
      className="mt-8 border border-slate-200 bg-white"
    >
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
        <Calendar className="h-3 w-3" />
        Google Calendar
      </div>
      <div className="space-y-4 p-5">
        <p className="text-sm text-slate-600">
          Connect Google Calendar to book interview holds in one click, with a
          Google Meet link generated automatically. The candidate gets a calendar
          invite to their email.
        </p>

        {!status?.platform_enabled ? (
          <div
            data-testid="calendar-not-configured"
            className="border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            Google Calendar is not configured on this server yet. Ask your admin
            to set <code className="font-mono">GOOGLE_CLIENT_ID</code> and{" "}
            <code className="font-mono">GOOGLE_CLIENT_SECRET</code> in the
            backend environment, then refresh this page.
          </div>
        ) : status?.connected ? (
          <div className="space-y-3">
            <div
              data-testid="calendar-connected-state"
              className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <div>
                Connected as{" "}
                <span className="font-semibold">{status.google_email}</span>
              </div>
            </div>
            <Button
              data-testid="calendar-disconnect-btn"
              variant="outline"
              onClick={disconnect}
              className="border-slate-300 text-slate-700"
            >
              <Unlink className="mr-2 h-4 w-4" />
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            data-testid="calendar-connect-btn"
            onClick={connect}
            disabled={connecting}
            className="bg-blue-600 text-white hover:bg-blue-500"
          >
            {connecting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="mr-2 h-4 w-4" />
            )}
            Connect Google Calendar
          </Button>
        )}
      </div>
    </div>
  );
}
